// Política de retención y purga de datos (LOPDP Art. 12/15).
// Sistemas → Gestión de Autenticación → Retención de datos. Solo admin (nivel>=5).
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Eye, Loader2, Timer, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { Switch, fieldCls } from './shared';
import { LOCALE } from '@/lib/utils/regional';

interface Politica { id: string; ambito: string; periodo_meses: number; descripcion: string | null; activo: boolean }
interface DetallePol { ambito: string; periodo_meses: number; cutoff?: string; afectados?: number; error?: string }
interface Preview { dry_run: boolean; total: number; politicas: DetallePol[] }
interface Ejecucion { id: string; ejecutado_at: string; total: number; detalle: DetallePol[] }

const rpc = async <T,>(fn: string, body: Record<string, unknown> = {}): Promise<T> =>
  (await apiClient.post<T>(`/rpc/${fn}`, body)).data;

const AMBITOS: Record<string, string> = {
  auditoria: 'Bitácoras de auditoría',
  notificaciones: 'Notificaciones in-app',
  mensajes_tele: 'Chat de teleconsulta',
  borrados: 'Registros borrados (purga física)',
  visitantes: 'Registro de visitantes',
  mensajes_crm: 'Mensajes de WhatsApp (CRM)',
  adjuntos_crm: 'Adjuntos de WhatsApp (CRM)',
};
const fmt = (s: string) => new Date(s).toLocaleString(LOCALE, { dateStyle: 'medium', timeStyle: 'short' });

export function RetencionDatos() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: politicas = [], isFetching, error } = useQuery<Politica[]>({ queryKey: ['retencion-pol'], queryFn: () => rpc<Politica[]>('retencion_politicas') });
  const { data: ejecuciones = [] } = useQuery<Ejecucion[]>({ queryKey: ['retencion-ejec'], queryFn: () => rpc<Ejecucion[]>('retencion_ejecuciones') });
  const [preview, setPreview] = useState<Preview | null>(null);

  const guardar = useMutation({
    mutationFn: (p: { ambito: string; periodo_meses: number; activo: boolean }) => rpc('retencion_politica_guardar', { _data: p }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['retencion-pol'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const previsualizar = useMutation({
    mutationFn: () => rpc<Preview>('retencion_previsualizar'),
    onSuccess: (d) => setPreview(d),
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const ejecutar = useMutation({
    mutationFn: () => rpc<Preview>('retencion_ejecutar'),
    onSuccess: (d) => { toast.success(`Purga ejecutada: ${d.total} registro(s) eliminado(s)`); setPreview(null); void qc.invalidateQueries({ queryKey: ['retencion-ejec'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (error) return <div className="p-6 text-sm text-urgente">{getErrorMessage(error)}</div>;
  const hayActivas = politicas.some((p) => p.activo);

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div>
        <h1 className="text-xl font-semibold flex items-center gap-2"><Timer className="w-5 h-5 text-primary" /> Retención y purga de datos</h1>
        <p className="text-sm text-muted-foreground">Define cuánto se conservan los datos y purga lo vencido (LOPDP Art. 12 y 15). La purga es irreversible: actívala con criterio.</p>
      </div>

      <Card>
        <CardContent className="overflow-x-auto pt-4">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Ámbito</th><th className="py-2 pr-3">Conservar (meses)</th><th className="py-2 pr-3">Activa</th><th className="py-2">Descripción</th>
            </tr></thead>
            <tbody>
              {politicas.map((p) => <FilaPolitica key={p.id} pol={p} onSave={guardar.mutate} />)}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <div className="flex flex-wrap gap-2">
        <Button variant="outline" isLoading={previsualizar.isPending} onClick={() => previsualizar.mutate()}><Eye className="w-4 h-4 mr-1" /> Previsualizar</Button>
        <Button className="bg-red-600 hover:bg-red-700" disabled={!hayActivas} isLoading={ejecutar.isPending}
          onClick={async () => { if (await dlg.confirmar('¿Ejecutar la purga ahora?', { detalle: 'Esta acción es irreversible y eliminará los datos vencidos según las políticas activas.', peligro: true, aceptar: 'Ejecutar purga' })) ejecutar.mutate(); }}>
          <Trash2 className="w-4 h-4 mr-1" /> Ejecutar purga
        </Button>
        {!hayActivas && <span className="self-center text-xs text-muted-foreground">No hay políticas activas; nada se purgará.</span>}
      </div>

      {preview && (
        <Card>
          <CardContent className="pt-4">
            <h3 className="font-medium mb-2">Previsualización — {preview.total} registro(s) a purgar</h3>
            {preview.politicas.length === 0 ? <p className="text-sm text-muted-foreground">No hay políticas activas.</p> : (
              <ul className="text-sm divide-y">
                {preview.politicas.map((d, i) => (
                  <li key={i} className="flex justify-between py-1.5">
                    <span>{AMBITOS[d.ambito] ?? d.ambito} <span className="text-xs text-muted-foreground">(&gt; {d.periodo_meses} meses)</span></span>
                    <span>{d.error ? <span className="text-urgente">error: {d.error}</span> : <b>{d.afectados}</b>}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      )}

      <div>
        <h3 className="text-sm font-semibold text-muted-foreground mb-2">Historial de purgas</h3>
        {ejecuciones.length === 0 ? <p className="text-sm text-muted-foreground">Sin purgas ejecutadas.</p> : (
          <ul className="text-sm divide-y border rounded-md">
            {ejecuciones.map((e) => (
              <li key={e.id} className="flex items-center justify-between px-3 py-2">
                <span>{fmt(e.ejecutado_at)}</span>
                <span className="text-muted-foreground">{e.total} registro(s) eliminado(s)</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function FilaPolitica({ pol, onSave }: { pol: Politica; onSave: (p: { ambito: string; periodo_meses: number; activo: boolean }) => void }) {
  const [meses, setMeses] = useState(pol.periodo_meses);
  const dirty = meses !== pol.periodo_meses;
  return (
    <tr className="border-b last:border-0 align-top">
      <td className="py-2 pr-3 font-medium">{AMBITOS[pol.ambito] ?? pol.ambito}</td>
      <td className="py-2 pr-3">
        <span className="inline-flex items-center gap-2">
          <input type="number" min={1} className={`${fieldCls} w-20`} value={meses} onChange={(e) => setMeses(Number(e.target.value))} />
          {dirty && <Button size="sm" variant="outline" onClick={() => onSave({ ambito: pol.ambito, periodo_meses: meses, activo: pol.activo })}>Guardar</Button>}
        </span>
      </td>
      <td className="py-2 pr-3"><Switch checked={pol.activo} onChange={(v) => onSave({ ambito: pol.ambito, periodo_meses: meses, activo: v })} label="" /></td>
      <td className="py-2 text-muted-foreground">{pol.descripcion}</td>
    </tr>
  );
}
